import { useRef, useState } from 'react'
import { Box, ButtonBase, Typography } from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import RotateRightIcon from '@mui/icons-material/RotateRight'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import { ManualRotation } from '../../../../../features/prescription'
import { localStorageWrapper } from '../../../../../helpers/localStorage'
import { NaturalSize } from '../../../../../helpers/imageRotation'
import { PrescImage } from '../../../../../types/models/PrescImage'
import { ImageArea } from '../../../../../types/models/Presc'
import { colorTokens } from '../../../../../shared/styles/theme'
import { RADIUS } from '../../../../../shared/styles/radii'
import { clampOffset, getImageLayout, Offset, zoomIn, zoomOut } from './deskew'

interface Props {
  images: PrescImage[]
  /** 処方箋存在領域(ページごと)。自動傾き補正に使う。QR 直読のレコードでは空 */
  imageArea: ImageArea[]
}

/**
 * 表示中の 1 枚に属する一時状態。**鍵は画像の id で、URL ではない。** ページを送れば初期値に戻る。
 *
 * `image_url` は CloudFront の署名付き URL で、backend が有効期限に `now` を入れて署名するため
 * (common/storage.py `generate_signed_url`)、**同じ画像でも取得のたびに文字列が変わる**。
 * URL を鍵にすると、一覧を取り直しただけで倍率・回転・パンが消え、`naturalSize` も失われる。
 * naturalSize が無い間 deskew.ts は補正なしの生の版面を返すので、器の縦横比ごと一度崩れて
 * 読み込み後に戻る = 画面が 2 回跳ねる。
 *
 * 画像は Prescription ではなく OCRRequestEvent にぶら下がっており(serializers.py
 * `get_presc_images`)、値を修正して版が上がっても作り直されない。**id は版をまたいで不変**。
 */
type PageView = {
  imageId: string
  naturalSize: NaturalSize | null
  manualRotation: ManualRotation
  scale: number
  offset: Offset
}
const EMPTY: Omit<PageView, 'imageId'> = {
  naturalSize: null,
  manualRotation: 0,
  scale: 1,
  offset: { x: 0, y: 0 },
}

const ControlButton = ({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string
  disabled?: boolean
  onClick: () => void
  children: React.ReactNode
}) => (
  <ButtonBase
    aria-label={label}
    disabled={disabled}
    onClick={onClick}
    sx={{
      width: 24,
      height: 24,
      flexShrink: 0,
      borderRadius: `${RADIUS.sm}px`,
      color: disabled ? colorTokens.fg.disabled : colorTokens.fg.muted,
      '&:hover': { background: colorTokens.overlay.hover },
    }}
  >
    {children}
  </ButtonBase>
)

/**
 * 処方箋画像。**操作列は帯にせず、画像の右上に重ねる**(常時表示)。帯にすると操作しない間も
 * 高さぶんの余白が残るため(04-apps/mamoru.md「画像ペインの器」)。
 *
 * 拡大はモーダルにせず、この枠の中でズーム + ドラッグして読みたい欄まで寄せる(ADR-0031)。
 * 状態は `scale` と `offset` の2つだけで、**等倍に戻す**のが「閉じる」の代わりになる。
 * ズームとパンは**回転の外側の器**に掛けている。回転済みの画像に直接掛けると
 * 画面のドラッグ方向と画像の移動方向がずれる(90°回転中は横ドラッグが縦移動になる)。
 *
 * 自動傾き補正と手動回転の style は共有の核から組み立てる(deskew.ts)。
 */
export const ImagePane = ({ images, imageArea }: Props) => {
  const [pageView, setPageView] = useState<PageView | null>(null)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const frameRef = useRef<HTMLDivElement | null>(null)
  const boxRef = useRef<HTMLDivElement | null>(null)
  const dragRef = useRef<{
    pointerId: number
    startX: number
    startY: number
    startOffset: Offset
  } | null>(null)

  // ページ数が減っても枠ごと消えないように丸める
  const currentIndex = Math.min(selectedIndex, Math.max(images.length - 1, 0))
  const image = images[currentIndex]
  const imageId = image?.id
  const url = image?.image_url
  const view = pageView && pageView.imageId === imageId ? pageView : EMPTY

  const update = (patch: Partial<Omit<PageView, 'imageId'>>) => {
    if (!imageId) return
    setPageView((prev) => ({
      ...(prev?.imageId === imageId ? prev : EMPTY),
      ...patch,
      imageId,
    }))
  }

  const layout = getImageLayout({
    area: imageArea[currentIndex],
    naturalSize: view.naturalSize,
    manualRotation: view.manualRotation,
    isImageRotatable: !!localStorageWrapper.getItem('isImageRotatable'),
  })

  const fitOffset = (offset: Offset, scale: number) => {
    const frame = frameRef.current
    const box = boxRef.current
    if (!frame || !box) return offset
    return clampOffset(offset, {
      frame: { x: frame.clientWidth, y: frame.clientHeight },
      box: { x: box.offsetWidth, y: box.offsetHeight },
      scale,
    })
  }

  const applyScale = (scale: number) =>
    update({
      scale,
      offset: scale === 1 ? { x: 0, y: 0 } : fitOffset(view.offset, scale),
    })

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (view.scale <= 1) return
    event.currentTarget.setPointerCapture(event.pointerId)
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startOffset: view.offset,
    }
    setIsDragging(true)
  }

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return
    update({
      offset: fitOffset(
        {
          x: drag.startOffset.x + (event.clientX - drag.startX),
          y: drag.startOffset.y + (event.clientY - drag.startY),
        },
        view.scale
      ),
    })
  }

  const handlePointerEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId !== event.pointerId) return
    dragRef.current = null
    setIsDragging(false)
  }

  if (!url) {
    return (
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography sx={{ fontSize: 13, color: colorTokens.fg.faint }}>
          画像がありません
        </Typography>
      </Box>
    )
  }

  const hasMultiplePages = images.length > 1
  const isZoomed = view.scale > 1

  return (
    <Box
      ref={frameRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
      sx={{
        flex: 1,
        minWidth: 0,
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 1,
        background: colorTokens.bg.subtle,
        touchAction: 'none',
        cursor: isZoomed ? (isDragging ? 'grabbing' : 'grab') : 'default',
      }}
    >
      <Box
        ref={boxRef}
        style={{
          position: 'relative',
          overflow: 'hidden',
          maxWidth: '100%',
          maxHeight: '100%',
          ...(layout.containerAspectRatio
            ? { height: '100%', aspectRatio: layout.containerAspectRatio }
            : { width: '100%' }),
          transform: `translate(${view.offset.x}px, ${view.offset.y}px) scale(${view.scale})`,
          transformOrigin: 'center center',
        }}
      >
        <img
          src={url}
          alt="処方箋"
          draggable={false}
          style={layout.imageStyle}
          onLoad={(event) => {
            const target = event.target as HTMLImageElement
            update({
              naturalSize: {
                w: target.naturalWidth,
                h: target.naturalHeight,
              },
            })
          }}
        />
      </Box>

      <Box
        // 紙面の上でドラッグが始まらないようにする(操作列の上は掴む面ではない)
        onPointerDown={(event) => event.stopPropagation()}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          height: 28,
          px: 0.5,
          borderRadius: `${RADIUS.md}px`,
          background: colorTokens.bg.default,
          border: `1px solid ${colorTokens.border.default}`,
          boxShadow: colorTokens.shadow.cardSubtle,
        }}
      >
        {isZoomed && (
          <ButtonBase
            onClick={() => applyScale(1)}
            sx={{
              height: 22,
              px: 0.75,
              mr: 0.25,
              borderRadius: `${RADIUS.sm}px`,
              fontSize: 12,
              color: colorTokens.fg.muted,
              '&:hover': { background: colorTokens.overlay.hover },
            }}
          >
            等倍に戻す
          </ButtonBase>
        )}
        {hasMultiplePages && (
          <>
            <ControlButton
              label="前のページ"
              onClick={() =>
                setSelectedIndex(
                  currentIndex === 0 ? images.length - 1 : currentIndex - 1
                )
              }
            >
              <ChevronLeftIcon sx={{ fontSize: 16 }} />
            </ControlButton>
            <Typography
              sx={{ fontSize: 12, color: colorTokens.fg.muted, px: 0.25 }}
            >
              {`${currentIndex + 1}/${images.length}`}
            </Typography>
            <ControlButton
              label="次のページ"
              onClick={() =>
                setSelectedIndex(
                  currentIndex === images.length - 1 ? 0 : currentIndex + 1
                )
              }
            >
              <ChevronRightIcon sx={{ fontSize: 16 }} />
            </ControlButton>
          </>
        )}
        <ControlButton
          label="回転"
          disabled={!view.naturalSize}
          onClick={() =>
            update({
              manualRotation: ((view.manualRotation + 90) %
                360) as ManualRotation,
              offset: { x: 0, y: 0 },
            })
          }
        >
          <RotateRightIcon sx={{ fontSize: 16 }} />
        </ControlButton>
        <ControlButton
          label="縮小"
          disabled={!isZoomed}
          onClick={() => applyScale(zoomOut(view.scale))}
        >
          <RemoveIcon sx={{ fontSize: 16 }} />
        </ControlButton>
        <Typography
          sx={{
            fontSize: 12,
            color: colorTokens.fg.muted,
            minWidth: 34,
            textAlign: 'center',
          }}
        >
          {`${Math.round(view.scale * 100)}%`}
        </Typography>
        <ControlButton
          label="拡大"
          onClick={() => applyScale(zoomIn(view.scale))}
        >
          <AddIcon sx={{ fontSize: 16 }} />
        </ControlButton>
      </Box>
    </Box>
  )
}
